import api from './api';

/**
 * Programme Stations (lot 031) : appels des écrans Cuves et Réception.
 *
 * Chemins et formes de réponse : `backend/app/api/v1/station_wetstock.py`.
 * Une création porte un identifiant choisi ici (clé d'idempotence du
 * serveur), une étape de réception une clé `Idempotency-Key` : un renvoi après
 * coupure rend le même résultat, sans seconde écriture de stock. Les chiffres
 * enregistrés viennent du serveur ; l'aperçu passe par `stationCalc.js`.
 */

const unwrap = (response) => response?.data?.data ?? response?.data;

/** Total d'une liste paginée par le serveur: en-tête `X-Total-Count`. */
const entierEnTete = (reponse) => {
  const brut = reponse?.headers?.['x-total-count'];
  if (brut === undefined || brut === null) return null;
  const nombre = Number.parseInt(brut, 10);
  return Number.isNaN(nombre) ? null : nombre;
};

export const nouvelId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;

/** Retire les champs absents ou vides d'un corps de requête. */
const sansVides = (corps) =>
  Object.fromEntries(Object.entries(corps || {}).filter(([, valeur]) => valeur !== undefined && valeur !== ''));

const avecCle = (cle) => ({ headers: { 'Idempotency-Key': cle || nouvelId() } });

/** Une pige : `{ height_cm, temperature_c, volume_liters, photo_document_id }` sans les vides. */
export const pige = (mesure) => {
  if (!mesure) return undefined;
  const corps = sansVides(mesure);
  return Object.keys(corps).length ? corps : undefined;
};

export const wetstockApi = {
  /** Cuves, dernier jaugeage, autonomie, réceptions ouvertes, fermetures à jauger. */
  async overview(stationId) {
    return unwrap(await api.get(`/stations/${stationId}/wetstock`));
  },

  async autonomy(stationId) {
    return unwrap(await api.get(`/stations/${stationId}/autonomy`)) || [];
  },

  /** Arrêt de journée : jaugeages de fermeture manquants et écart de chaque cuve. */
  async tankDay(stationId, date) {
    return unwrap(await api.get(`/stations/${stationId}/tank-day`, { params: { date } }));
  },

  async reconciliation(stationId, tankId, { dateFrom, dateTo } = {}) {
    return unwrap(
      await api.get(`/stations/${stationId}/tanks/${tankId}/reconciliation`, {
        params: sansVides({ date_from: dateFrom, date_to: dateTo }),
      }),
    );
  },

  dips: {
    async list(stationId, { tankId, dateFrom, dateTo, kind, limit = 100 } = {}) {
      return (
        unwrap(
          await api.get(`/stations/${stationId}/dips`, {
            params: sansVides({ tank_id: tankId, date_from: dateFrom, date_to: dateTo, kind, limit }),
          }),
        ) || []
      );
    },
    /** Une page de jaugeages (`limit` par page, 100 au plus) : `{ items, total }`. */
    async page(stationId, { tankId, dateFrom, dateTo, kind, page = 1, limit = 20 } = {}) {
      const reponse = await api.get(`/stations/${stationId}/dips`, {
        params: sansVides({ tank_id: tankId, date_from: dateFrom, date_to: dateTo, kind, page, limit }),
      });
      const items = unwrap(reponse) || [];
      return { items, total: entierEnTete(reponse) ?? items.length };
    },
    /** `data` : tank_id, kind (DAY_OPEN|DAY_CLOSE|CONTROL), height_cm ou volume_liters, temperature_c… */
    async record(stationId, data) {
      return unwrap(await api.post(`/stations/${stationId}/dips`, sansVides({ ...data, id: data.id || nouvelId() })));
    },
  },

  receptions: {
    async list(stationId, { status, limit = 50 } = {}) {
      const statuts = Array.isArray(status) ? status.join(',') : status;
      return unwrap(await api.get(`/stations/${stationId}/receptions`, { params: sansVides({ status: statuts, limit }) })) || [];
    },
    /** Une page de réceptions (`limit` par page, 100 au plus) : `{ items, total }`. */
    async page(stationId, { status, page = 1, limit = 20 } = {}) {
      const statuts = Array.isArray(status) ? status.join(',') : status;
      const reponse = await api.get(`/stations/${stationId}/receptions`, {
        params: sansVides({ status: statuts, page, limit }),
      });
      const items = unwrap(reponse) || [];
      return { items, total: entierEnTete(reponse) ?? items.length };
    },
    async get(receptionId) {
      return unwrap(await api.get(`/station-receptions/${receptionId}`));
    },
    async plan(stationId, data) {
      return unwrap(await api.post(`/stations/${stationId}/receptions`, sansVides({ ...data, id: data.id || nouvelId() })));
    },
    /** Début du dépotage : `{ dip_before, truck_arrival_liters_15c, seals_ok, seal_numbers… }`. */
    async start(receptionId, data, cle) {
      return unwrap(
        await api.post(
          `/station-receptions/${receptionId}/start`,
          sansVides({ ...data, dip_before: pige(data?.dip_before) }),
          avecCle(cle),
        ),
      );
    },
    /** Validation : `{ dip_after, seals_ok, photos, truck_arrival_liters_15c… }`. */
    async validate(receptionId, data, cle) {
      return unwrap(
        await api.post(
          `/station-receptions/${receptionId}/validate`,
          sansVides({ ...data, dip_after: pige(data?.dip_after) }),
          avecCle(cle),
        ),
      );
    },
    async cancel(receptionId, reason) {
      return unwrap(await api.post(`/station-receptions/${receptionId}/cancel`, { reason }));
    },
  },

  /** Ajustement motivé : `liters` positif (ajout) ou négatif (retrait). */
  async adjust(tankId, { liters, reason, id }) {
    return unwrap(await api.post(`/stations/tanks/${tankId}/adjustments`, { id: id || nouvelId(), liters, reason }));
  },
};

export default wetstockApi;
